import {
  Cog6ToothIcon,
  UserGroupIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import {
  Container,
  Group,
  Modal,
  Stack,
  Tabs,
  Text,
  Title,
  rem,
} from "@mantine/core";
import { useState } from "react";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import { TeamAssignments } from "../../components/feature-specific/TeamAssignments";
import { TeamDirectory } from "../../components/feature-specific/TeamDirectory";
import { TeamForm } from "../../components/feature-specific/TeamForm";
import { TeamSettings } from "../../components/feature-specific/TeamSettings";
import { useTranslation } from "../../hooks/useTranslation";
import { Team, TeamRole } from "../../types/team";

export function OperationsTeams() {
  const [activeTab, setActiveTab] = useState<string | null>("directory");
  const [teamFormOpened, setTeamFormOpened] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | undefined>(undefined);
  const { t } = useTranslation();

  // Function to render icons with consistent styling
  const renderIcon = (Icon: any) => (
    <Icon
      style={{
        width: rem(48),
        height: rem(48),
        color: "var(--mantine-color-gray-7)",
      }}
    />
  );

  // Handle creating a team
  const handleCreateTeam = () => {
    setEditingTeam(undefined);
    setTeamFormOpened(true);
  };

  // Handle editing a team
  const handleEditTeam = (team: Team) => {
    setEditingTeam(team);
    setTeamFormOpened(true);
  };

  // Handle deleting a team
  const handleDeleteTeam = (teamId: string) => {
    console.log("Delete team:", teamId);
    // In a real app, you would call an API to delete the team
    alert(t("ADMIN_TEAMS_CONFIRM_DELETE"));
  };

  // Handle team form submission
  const handleTeamFormSubmit = (
    teamData: Omit<Team, "id" | "createdAt" | "updatedAt">
  ) => {
    if (editingTeam) {
      console.log("Update team:", { ...editingTeam, ...teamData });
      // In a real app, you would call an API to update the team
      alert(t("ADMIN_TEAMS_EDIT") + ": " + teamData.name);
    } else {
      console.log("Create team:", teamData);
      // In a real app, you would call an API to create the team
      alert(t("ADMIN_TEAMS_ADD") + ": " + teamData.name);
    }
    setTeamFormOpened(false);
  };

  // Handle adding a member to a team
  const handleAddTeamMember = (
    teamId: string,
    memberId: string,
    role: TeamRole
  ) => {
    console.log("Add member to team:", { teamId, memberId, role });
    // In a real app, you would call an API to add the member
    alert(`${t("ADMIN_TEAMS_MEMBERS")}: ${memberId} → ${teamId} (${role})`);
  };

  // Handle removing a member from a team
  const handleRemoveTeamMember = (teamId: string, memberId: string) => {
    console.log("Remove member from team:", { teamId, memberId });
    // In a real app, you would call an API to remove the member
    alert(
      `${t("COMMON_DELETE")} ${t(
        "ADMIN_TEAMS_MEMBERS"
      )}: ${memberId} ← ${teamId}`
    );
  };

  // Handle saving team settings
  const handleSaveTeamSettings = (teamId: string, settings: any) => {
    console.log("Save team settings:", { teamId, settings });
    // In a real app, you would call an API to save the settings
    alert(`${t("COMMON_SAVE")} ${t("ADMIN_TEAMS_TITLE")}: ${teamId}`);
  };

  return (
    <Container size="lg" py="xl">
      <Stack gap="xs" mb="xl">
        <Breadcrumb
          items={[
            { label: "Operations Panel", href: "/operations" },
            { label: "Teams", isActive: true },
          ]}
        />
        <Group justify="space-between" align="flex-end">
          <div>
            <Title order={1} size="h2" fw={700}>
              {t("ADMIN_TEAMS_TITLE")}
            </Title>
            <Text c="dimmed" size="lg">
              {t("ADMIN_TEAMS_DESCRIPTION")}
            </Text>
          </div>
        </Group>
      </Stack>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab
            value="directory"
            leftSection={
              <UserGroupIcon style={{ width: rem(16), height: rem(16) }} />
            }
          >
            {t("ADMIN_TEAMS_TITLE")}
          </Tabs.Tab>
          <Tabs.Tab
            value="assignments"
            leftSection={
              <UserPlusIcon style={{ width: rem(16), height: rem(16) }} />
            }
          >
            {t("ADMIN_TEAMS_MEMBERS")}
          </Tabs.Tab>
          <Tabs.Tab
            value="settings"
            leftSection={
              <Cog6ToothIcon style={{ width: rem(16), height: rem(16) }} />
            }
          >
            {t("COMMON_SETTINGS")}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="directory">
          <TeamDirectory
            onCreateTeam={handleCreateTeam}
            onEditTeam={handleEditTeam}
            onDeleteTeam={handleDeleteTeam}
          />
        </Tabs.Panel>

        <Tabs.Panel value="assignments">
          <TeamAssignments
            onAddMember={handleAddTeamMember}
            onRemoveMember={handleRemoveTeamMember}
          />
        </Tabs.Panel>

        <Tabs.Panel value="settings">
          <TeamSettings onSaveSettings={handleSaveTeamSettings} />
        </Tabs.Panel>
      </Tabs>

      {/* Modal for creating/editing teams */}
      <Modal
        opened={teamFormOpened}
        onClose={() => setTeamFormOpened(false)}
        title={editingTeam ? t("ADMIN_TEAMS_EDIT") : t("ADMIN_TEAMS_ADD")}
        size="lg"
      >
        <TeamForm
          team={editingTeam}
          onSubmit={handleTeamFormSubmit}
          onCancel={() => setTeamFormOpened(false)}
        />
      </Modal>
    </Container>
  );
}
